/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  IconClose,
  NamedLink,
  NotificationBadge,
} from '../../../../components';
import IconHeart from '../../../../components/IconHeart/IconHeart';
import { ReactComponent as InboxIcon } from '../../../../assets/InboxIcon.svg';
import { ReactComponent as ParachuteIcon } from '../../../../assets/ParachuteIcon.svg';
import { ReactComponent as BellIcon } from '../../../../assets/BellOutline.svg';
import { ReactComponent as ProfileIcon } from '../../../../assets/ProfileIcon.svg';
import { ReactComponent as SettingsIcon } from '../../../../assets/SettingsIcon.svg';
import { ReactComponent as LogoutIcon } from '../../../../assets/LogoutIcon.svg';
import { ReactComponent as DropdownIcon } from '../../../../assets/DropdownIcon.svg';
import aventoLogo from '../../../../assets/Avento schwarz.png';

import css from './TopbarMobileMenu.module.css';
import usePreventScrollBounce from '../../../../hooks/usePreventScrollBounce';

const LinkContent = props => {
  const { icon, label, badge } = props;
  return (
    <span className={css.linkContent}>
      {icon || <span className={css.menuItemBorder} />}
      <span className={css.linkLabel}>{label}</span>
      {badge ? <span className={css.linkBadge}>{badge}</span> : null}
    </span>
  );
};

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <LinkContent label={text} />
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <LinkContent label={text} />
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = forwardRef((props, ref) => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks = [],
    onLogout,
    showCreateListingsLink,
    showManageListingsLink = showCreateListingsLink,
    filterLinks,
    variant = 'default',
    onClose,
  } = props;

  const intl = useIntl();
  const menuRef = useRef(null);
  usePreventScrollBounce(menuRef);

  const [closing, setClosing] = useState(false);

  const triggerClose = useCallback(() => {
    setClosing(true);
    const scrollLayer = menuRef.current?.closest('[class*="modalQuickScrollLayer"]');
    if (scrollLayer) {
      scrollLayer.classList.add('quickMenuOverlayClosing');
    }
  }, []);

  useImperativeHandle(ref, () => ({ triggerClose }), [triggerClose]);

  const handleAnimationEnd = useCallback((e) => {
    if (closing && e.target === menuRef.current) {
      setClosing(false);
      const scrollLayer = menuRef.current?.closest('[class*="modalQuickScrollLayer"]');
      if (scrollLayer) {
        scrollLayer.classList.remove('quickMenuOverlayClosing');
      }
      onClose?.();
    }
  }, [closing, onClose]);
  const user = ensureCurrentUser(currentUser);

  const menuLinks = Array.isArray(customLinks) ? customLinks : [];
  const filteredLinks = filterLinks ? filterLinks(menuLinks) : menuLinks;

  const renderLinks = links =>
    links.map((linkConfig, index) => (
      <CustomLinkComponent
        key={`${linkConfig?.text || linkConfig?.href || index}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    ));

  const extraLinks = renderLinks(filteredLinks);

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  const closeButtonMaybe = onClose ? (
    <button type="button" className={css.closeButton} onClick={triggerClose} aria-label="Close menu">
      <IconClose rootClassName={css.closeIcon} />
    </button>
  ) : null;

  if (variant === 'linksOnly') {
    return (
      <nav className={classNames(css.root, css.linksOnly, { [css.rootClosing]: closing })} onAnimationEnd={handleAnimationEnd} ref={menuRef}>
        <div className={css.topRow}>{closeButtonMaybe}</div>
        <div className={css.contentLinksOnly}>
          <div className={css.customLinksWrapper}>{extraLinks}</div>
        </div>
        {showCreateListingsLink ? <div className={css.footer}>{createListingsLinkMaybe}</div> : null}
      </nav>
    );
  }

  if (!isAuthenticated) {
    return (
      <nav className={classNames(css.root, { [css.rootClosing]: closing })} onAnimationEnd={handleAnimationEnd} ref={menuRef}>
        <div className={css.topRow}>{closeButtonMaybe}</div>
        <div className={css.authBranding}>
          <img src={aventoLogo} alt="Avento" className={css.authLogo} />
        </div>
        <div className={classNames(css.content, css.authContent)}>
          <div className={css.authLinks}>
            <NamedLink name="LoginPage" className={classNames(css.authLink, css.loginLink)}>
              <FormattedMessage id="TopbarMobileMenu.loginLink" />
            </NamedLink>
            <NamedLink name="SignupPage" className={classNames(css.authLink, css.signupLink)}>
              <FormattedMessage id="TopbarMobileMenu.signupLink" />
            </NamedLink>
          </div>
          <div className={css.customLinksWrapper}>{extraLinks}</div>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const isAccountSettingsPage = ACCOUNT_SETTINGS_PAGES.includes(currentPage);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(isAccountSettingsPage);

  useEffect(() => {
    if (isAccountSettingsPage) {
      setIsAccountMenuOpen(true);
    }
  }, [isAccountSettingsPage]);

  const accountSettingsLinks = [
    {
      name: 'ContactDetailsPage',
      messageId: 'LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle',
    },
    {
      name: 'PasswordChangePage',
      messageId: 'LayoutWrapperAccountSettingsSideNav.passwordTabTitle',
    },
    {
      name: 'StripePayoutPage',
      messageId: 'LayoutWrapperAccountSettingsSideNav.paymentsTabTitle',
    },
    {
      name: 'PaymentMethodsPage',
      messageId: 'LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle',
    },
  ];

  const accountSubmenuId = 'TopbarMobileMenuAccountSettingsSubmenu';
  const toggleAccountMenu = () => setIsAccountMenuOpen(prev => !prev);
  const accountSettingsToggleLabel = intl.formatMessage({
    id: 'TopbarMobileMenu.accountSettingsToggleLabel',
  });
  const handleAccountSettingsClick = event => {
    event.preventDefault();
    toggleAccountMenu();
  };

  const manageListingsLinkMaybe = showManageListingsLink ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <LinkContent
        icon={<ParachuteIcon className={css.linkIcon} aria-hidden="true" />}
        label={<FormattedMessage id="TopbarMobileMenu.yourListingsLink" />}
      />
    </NamedLink>
  ) : null;

  const favouritesLink = (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('FavouritesPage'))}
      name="FavouritesPage"
    >
      <LinkContent
        icon={<IconHeart className={css.linkIcon} />}
        label={<FormattedMessage id="TopbarMobileMenu.favouritesLink" />}
      />
    </NamedLink>
  );

  const wishlistLink = (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('WishlistPage'))}
      name="WishlistPage"
    >
      <LinkContent
        icon={<BellIcon className={css.linkIcon} aria-hidden="true" />}
        label={<FormattedMessage id="TopbarMobileMenu.wishlistLink" />}
      />
    </NamedLink>
  );

  return (
    <div className={classNames(css.root, { [css.rootClosing]: closing })} ref={menuRef} onAnimationEnd={handleAnimationEnd}>
      <div className={css.topRow}>{closeButtonMaybe}</div>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <LinkContent
              icon={<InboxIcon className={css.linkIcon} aria-hidden="true" />}
              label={<FormattedMessage id="TopbarMobileMenu.inboxLink" />}
              badge={notificationCountBadge}
            />
          </NamedLink>
          {manageListingsLinkMaybe}
          {favouritesLink}
          {wishlistLink}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <LinkContent
              icon={<ProfileIcon className={css.linkIcon} aria-hidden="true" />}
              label={<FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />}
            />
          </NamedLink>
          <div className={css.accountSettingsGroup}>
            <NamedLink
              className={classNames(
                css.navigationLink,
                css.accountSettingsLink,
                currentPageClass('AccountSettingsPage')
              )}
              name="AccountSettingsPage"
              onClick={handleAccountSettingsClick}
              aria-expanded={isAccountMenuOpen}
              aria-controls={accountSubmenuId}
            >
              <LinkContent
                icon={<SettingsIcon className={css.linkIcon} aria-hidden="true" />}
                label={<FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />}
              />
            </NamedLink>
            <button
              type="button"
              className={classNames(css.accountSettingsToggle, {
                [css.accountSettingsToggleOpen]: isAccountMenuOpen,
              })}
              onClick={toggleAccountMenu}
              aria-expanded={isAccountMenuOpen}
              aria-controls={accountSubmenuId}
              aria-label={accountSettingsToggleLabel}
            >
              <DropdownIcon className={css.accountSettingsToggleIcon} aria-hidden="true" />
            </button>
          </div>
          <div
            className={classNames(css.accountSubLinksWrapper, {
              [css.accountSubLinksWrapperOpen]: isAccountMenuOpen,
            })}
          >
            <ul
              className={css.accountSubLinks}
              id={accountSubmenuId}
              aria-hidden={!isAccountMenuOpen}
            >
              {accountSettingsLinks.map(link => (
                <li key={link.name} className={css.accountSubLinkItem}>
                  <NamedLink
                    className={classNames(css.accountSubLink, {
                      [css.accountSubLinkActive]: currentPage === link.name,
                    })}
                    name={link.name}
                    tabIndex={isAccountMenuOpen ? 0 : -1}
                  >
                    <LinkContent
                      label={<FormattedMessage id={link.messageId} />}
                    />
                  </NamedLink>
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            className={classNames(css.navigationLink, css.logoutButton)}
            onClick={onLogout}
          >
            <LinkContent
              icon={<LogoutIcon className={css.linkIcon} aria-hidden="true" />}
              label={<FormattedMessage id="TopbarMobileMenu.logoutLink" />}
            />
          </button>
        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
    </div>
  );
});

export default TopbarMobileMenu;
